import styled, { css } from 'styled-components';
import { useEffect } from 'react';

import { ClientOnlyPortal } from 'shared/components';

export interface ISnackbarNotification {
  children: React.ReactNode;
  duration?: number;
  isNotificationVisible: boolean;
  position?: 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  setIsNotificationVisible: (val: boolean) => void;
}

export const SnackbarNotification = ({
  children,
  duration = 3000,
  isNotificationVisible: isVisible,
  position = 'center',
  setIsNotificationVisible: setIsVisible,
}: ISnackbarNotification) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsVisible(false);
    }, duration);

    return () => {
      clearTimeout(timeout);
    };
  }, [duration, isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <ClientOnlyPortal selector="#main-page-layout">
      <SnackbarNotification.Wrapper
        data-qa="snackbar_notification"
        duration={duration}
        isVisible={isVisible}
        position={position}
      >
        {children}
      </SnackbarNotification.Wrapper>
    </ClientOnlyPortal>
  );
};

SnackbarNotification.Wrapper = styled.div<{
  duration: number;
  isVisible: boolean;
  position: ISnackbarNotification['position'];
}>`
  position: absolute;
  background: ${({ theme }) => theme.colors.white};
  border: ${({ theme }) => theme.borders.black};
  padding: 8px;
  max-width: 300px;
  z-index: ${({ theme }) => theme.zIndex.snackbarNotification};
  animation: fadeOut 0.5s ${({ duration }) => `${duration - 500}ms`};

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  ${({ position }) => {
    if (position === 'center') {
      return css`
        top: 66px; /* 50px for header, 16px from header */
        left: 50%;
        transform: translate(-50%, 0%);
      `;
    }

    if (position === 'topLeft') {
      return css`
        top: 66px; /* 50px for header, 16px from header */
        left: 16px;
      `;
    }

    if (position === 'topRight') {
      return css`
        top: 66px; /* 50px for header, 16px from header */
        right: 16px;
      `;
    }

    if (position === 'bottomLeft') {
      return css`
        bottom: 66px;
        left: 16px;
      `;
    }

    if (position === 'bottomRight') {
      return css`
        bottom: 66px;
        right: 16px;
      `;
    }
  }};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    max-width: 500px;
  }
`;
